import {createSelector} from 'reselect';
import {info, icons} from '../utils/errorEvents';
import {
    getListingPage,
    getTranslations
} from './store';
import {getArrivedFromExternalSiteWithoutDates} from './system';
import {
    getIncompleteDates,
    getDateSuccess,
    getDatesError
} from './tripDates';
import getPriceStatus from './priceStatus';
import {isInquiryModalOpen} from './modalStates';
import {getContactOwnerFormHasTripDetailsErrors} from './contactOwnerForm';
import {getGuestsError} from './tripGuests';

// We are currently showing one error at a time, so this will return the error that takes priority, not all of them.
const getCurrentError = createSelector(
    [getDatesError, getGuestsError, getPriceStatus],
    (datesError, guestsError, priceStatus) => {
        const {
            priceError
        } = priceStatus;

        return priceError || datesError || guestsError || null;
    }
);

// We are currently showing one error at a time, so this will return the error that takes priority, not all of them.
const getInquiryModalError = createSelector(
    [getGuestsError],
    (guestsError) => {
        let inquiryError = {};

        if (guestsError) {
            inquiryError = Object.assign({}, guestsError);
            inquiryError.icon = icons.DANGER;
        }

        return inquiryError;
    }
);

// Determines if an alert should be shown
const getShowAlert = createSelector(
    [getCurrentError, getIncompleteDates, getListingPage, getDateSuccess, isInquiryModalOpen, getArrivedFromExternalSiteWithoutDates],
    (currentError, incompleteDates, listingPage, dateSuccess, inquiryModalOpen, arrivedFromExternalSiteWithoutDates) => {
        if ((currentError && currentError.type === 'guests' || !!dateSuccess) && arrivedFromExternalSiteWithoutDates && !inquiryModalOpen) {
            return false;
        }

        const shouldShowOnMainPage = (!listingPage.loadingRate && (incompleteDates || !!currentError || !!dateSuccess)) || false;
        return shouldShowOnMainPage;
    }
);

// Determines if an alert should be shown
const getEnterDatesTimerComplete = createSelector(
    [getListingPage],
    (listingPage) => {
        return listingPage.enterDatesTimerComplete;
    }
);

// Determines if an alert should be shown
const getShowInquiryAlert = createSelector(
    [getContactOwnerFormHasTripDetailsErrors, isInquiryModalOpen, getShowAlert],
    (contactOwnerFormHasTripDetailsErrors, inquiryModalOpen, showAlert) => {
        const shouldShowOnMainPage = showAlert;
        const shouldShowInInquiryModal = contactOwnerFormHasTripDetailsErrors;
        return inquiryModalOpen ? shouldShowInInquiryModal : shouldShowOnMainPage;
    }
);

// Determines what alert should show
const getAlertInfo = createSelector(
    [getTranslations, getIncompleteDates, getDateSuccess, getCurrentError],
    (translations, incompleteDates, dateSuccess, currentError) => {
        let enterDatesForPricing = info.DATES_ACCURATE;
        enterDatesForPricing.label = translations[enterDatesForPricing.translationKey];

        const mainPageAlertInfo = incompleteDates ? enterDatesForPricing : (currentError || dateSuccess);

        return mainPageAlertInfo;
    }
);

// Determines what alert should show
const getInquiryAlertInfo = createSelector(
    [getTranslations, getIncompleteDates, getInquiryModalError, getDateSuccess, getCurrentError, isInquiryModalOpen],
    (translations, incompleteDates, inquiryModalError, dateSuccess, currentError, inquiryModalOpen) => {
        let enterDatesForPricing = info.DATES_ACCURATE;
        enterDatesForPricing.label = translations[enterDatesForPricing.translationKey];

        const mainPageAlertInfo = incompleteDates ? enterDatesForPricing : (currentError || dateSuccess);
        const inquiryModalAlertInfo = inquiryModalOpen ? inquiryModalError : null;

        return inquiryModalAlertInfo || mainPageAlertInfo;
    }
);

const getTripAlertData = createSelector(
    [getShowAlert, getAlertInfo, getIncompleteDates, getEnterDatesTimerComplete],
    (showAlert, alertInfo, incompleteDates, enterDatesTimerComplete) => {
        return {
            alertInfo,
            showAlert,
            enterDatesTimerComplete,
            showEnterDatesTooltip: incompleteDates
        };
    }
);

const getTripAlertDataForInquiry = createSelector(
    [getTripAlertData, getShowInquiryAlert, getInquiryAlertInfo],
    (tripDetails, showInquiryAlert, inquiryAlertInfo) => {
        const inquiryTripDetails = Object.assign({}, tripDetails, {
            showAlert: showInquiryAlert,
            alertInfo: inquiryAlertInfo,
            showEnterDatesTooltip: false
        });

        return inquiryTripDetails;
    });

export default getTripAlertData;
export {
    getCurrentError,
    getInquiryModalError,
    getShowAlert,
    getShowInquiryAlert,
    getAlertInfo,
    getInquiryAlertInfo,
    getTripAlertData,
    getTripAlertDataForInquiry
};
